/**
 * sl-9qm.9: SEO Template Engine — Secure template resolution with escaping
 *
 * Resolves {{key.subkey}} placeholders in SEO templates.
 * ALL user-supplied values are escaped before injection (HTML, SVG, JSON).
 *
 * SOLID:
 * - S: Only template resolution + escaping (no UI, no persistence)
 * - O: New escaping strategies can be added without modifying existing code
 */

/** HTML entity escaping for meta tags and OG tags */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/** SVG content escaping for Satori OG image generation */
export function escapeSvgContent(value: string): string {
  return escapeHtml(value).replace(/\{/g, '&#123;').replace(/\}/g, '&#125;');
}

/** JSON value escaping for JSON-LD script tags */
export function escapeJsonLd(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t')
    .replace(/<\/script/gi, '<\\/script');
}

/**
 * Get a nested value from an object using dot notation.
 * e.g. getNestedValue({ row: { title: 'Hello' } }, 'row.title') → 'Hello'
 */
export function getNestedValue(
  context: Record<string, unknown>,
  path: string
): string {
  const parts = path.split('.');
  let current: unknown = context;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[part];
  }
  return current != null ? String(current) : '';
}

/** Escape strategy for template resolution */
export type EscapeStrategy = 'html' | 'svg' | 'json';

const ESCAPE_FNS: Record<EscapeStrategy, (v: string) => string> = {
  html: escapeHtml,
  svg: escapeSvgContent,
  json: escapeJsonLd,
};

/**
 * Resolve template placeholders with escaped values.
 *
 * @param template - Template string with {{key.subkey}} placeholders
 * @param context - Data context for placeholder resolution
 * @param strategy - Escaping strategy (default: 'html')
 * @returns Resolved string with all placeholders replaced and escaped
 */
export function resolveTemplate(
  template: string,
  context: Record<string, unknown>,
  strategy: EscapeStrategy = 'html'
): string {
  const escapeFn = ESCAPE_FNS[strategy];
  return template.replace(
    /\{\{(\w+(?:\.\w+)*)\}\}/g,
    (_match: string, path: string) => {
      const raw = getNestedValue(context, path);
      return escapeFn(raw);
    }
  );
}

/**
 * Validate that a canonical URL is safe (HTTPS only, no javascript:/data:).
 * Returns the URL string if valid, null otherwise.
 */
export function validateCanonicalUrl(url: string): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return null;
    return parsed.href;
  } catch {
    return null;
  }
}
